import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { integrationsApi } from './api'
import type { CreateIntegrationSourceRequest, UpdateIntegrationSourceRequest } from './types'

export const integrationKeys = {
  sources: ['integration', 'sources'] as const,
  webhookLog: (sourceCode: string, limit: number) =>
    ['integration', 'webhook-log', sourceCode, limit] as const,
}

export function useIntegrationSources() {
  return useQuery({
    queryKey: integrationKeys.sources,
    queryFn: integrationsApi.listSources,
  })
}

export function useWebhookLog(sourceCode: string, limit = 50) {
  return useQuery({
    queryKey: integrationKeys.webhookLog(sourceCode, limit),
    queryFn: () => integrationsApi.webhookLog(sourceCode, limit),
    enabled: !!sourceCode,
  })
}

export function useFieldMappings(sourceCode: string) {
  return useQuery({
    queryKey: ['integration', 'field-mappings', sourceCode] as const,
    queryFn: () => integrationsApi.fieldMappings(sourceCode),
    enabled: !!sourceCode,
  })
}

export function useUpdateIntegrationSource() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ code, body }: { code: string; body: UpdateIntegrationSourceRequest }) =>
      integrationsApi.updateSource(code, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: integrationKeys.sources }),
  })
}

export function useCreateIntegrationSource() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: CreateIntegrationSourceRequest) => integrationsApi.createSource(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: integrationKeys.sources }),
  })
}
